import React, { useEffect, useState } from "react";
import clsx from "clsx";
import * as Yup from "yup";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import {
  getCourseReviewsAction,
  submitReviewCourseAction,
  clearCourseReviewsAction,
  getMoreCourseReviewsAction,
  updateReviewCourseAction,
  checkUserReviewAction,
  getCourseReviewSummaryAction,
  updateReviewCourseActions,
} from "@redux/modules/review/slices";
import {
  AccessControl,
  NoData,
  ReviewComment,
  ReviewCommentPlaceHolder,
  ReviewSummary,
  SpinnerCustom,
  StarRating,
  TextArea,
} from "@components";
import { Avatar } from "@components/common";
import { INSTRUCTOR_ROLE, STUDENT_ROLE } from "@constants";
import styles from "./style/ReviewsTab.module.css";

const validateSchema = Yup.object().shape({
  enrollmentId: Yup.string(),
  content: Yup.string().trim().required("Content is required"),
  rating: Yup.number().required("Rating is required"),
});

const initialValues = {
  enrollmentId: "",
  content: "",
  rating: "",
};

const placeholder = Array.from(Array(3).keys());

const ReviewsTab = (props) => {
  const { courseId, loadReviews, author } = props;
  // Redux
  const { data: user } = useSelector((state) => state.user.profile);
  const {
    getCourseReviews,
    submitReviewCourse,
    updateReviewCourse,
    checkUserReview,
    getCourseReviewSummary,
  } = useSelector((state) => state.review);
  const {
    reviews,
    processing: processingFetch,
    pagination,
    processingGetMore,
  } = getCourseReviews;
  const { ratingList, avgRating, totalReviews } = getCourseReviewSummary;
  const { processing: processingSubmit, hasError: submitError } =
    submitReviewCourse;
  const { hasError: updateError, processing: processingUpdate } =
    updateReviewCourse;
  const {
    userReview,
    prevReview,
    isSuccess: isCheckUserReviewSuccess,
    processing: processingCheck,
  } = checkUserReview;
  const { id: userReviewId } = userReview || {};
  const hasError = updateError || submitError;

  const isOwner = author && user ? author.id === user.id : false;

  const { data: enrollmentInfo, isEnrolled } = useSelector(
    (state) => state.enrollment.checkEnrollment
  );
  const dispatch = useDispatch();
  // const { isAuth } = useAccessControl();

  // State + effect
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [showForm, setShowForm] = useState(true);
  const [ratingSelected, setRatingSelected] = useState(0);

  useEffect(() => {
    if (loadReviews) {
      getAllData();
    }
    return () => {
      dispatch(clearCourseReviewsAction());
      resetForm();
    };
  }, [courseId, loadReviews]);

  useEffect(() => {
    if (isEnrolled === true && isCheckUserReviewSuccess === null) {
      dispatch(checkUserReviewAction(courseId));
    }
  }, [isEnrolled, isCheckUserReviewSuccess]);

  useEffect(() => {
    if (userReview && !isEditMode && !isCreateMode && !hasError) {
      setShowForm(false);
    } else {
      setShowForm(true);
    }
  }, [userReview, isEditMode, isCreateMode, hasError]);

  useEffect(() => {
    if (userReview) {
      setIsCreateMode(false);
      handleResetForm();
    }
  }, [userReview]);

  useEffect(() => {
    if (submitError) {
      setIsCreateMode(true);
    } else if (updateError) {
      setIsEditMode(true);
    }
  }, [updateError, submitError]);

  useEffect(() => {
    if (loadReviews) {
      getAllData();
    }
  }, [ratingSelected, loadReviews]);

  // Handle
  const getAllData = () => {
    dispatch(getCourseReviewsAction({ courseId, rating: ratingSelected }));
    dispatch(getCourseReviewSummaryAction(courseId));
    dispatch(checkUserReviewAction(courseId));
  };

  const handleResetForm = () => {
    resetForm({
      values: { content: userReview.content, rating: userReview.rating },
    });
  };

  const handleSeeMore = () => {
    dispatch(getMoreCourseReviewsAction({ courseId }));
  };

  const handleCancel = () => {
    setIsEditMode(false);
    dispatch(updateReviewCourseActions.resetError());
    resetForm({
      values: { content: prevReview.content, rating: prevReview.rating },
    });
    setShowForm(false);
  };

  const handleClickEdit = () => {
    setShowForm(true);
    setIsEditMode(true);
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    resetForm,
    values,
    errors,
    touched,
  } = useFormik({
    initialValues: initialValues,
    validationSchema: validateSchema,
    onSubmit: (values) => {
      if (isEditMode) {
        const payload = {
          review: { ...values, reviewId: userReviewId },
          courseId,
        };
        dispatch(updateReviewCourseAction(payload));
      } else {
        dispatch(
          submitReviewCourseAction({
            review: { ...values, enrollmentId: enrollmentInfo.id },
            courseId: courseId,
          })
        );
      }
      setIsCreateMode(false);
      setIsEditMode(false);
    },
  });

  // UI partials
  const editable =
    !processingCheck && !processingUpdate && !processingSubmit && !hasError;
  const submittable =
    values.rating !== userReview?.rating ||
    values.content !== userReview?.content;
  const formReview = enrollmentInfo && !isOwner && (
    <AccessControl targetRoles={[STUDENT_ROLE, INSTRUCTOR_ROLE]}>
      {showForm ? (
        <Form
          noValidate
          onSubmit={handleSubmit}
          className={clsx("m-0 mt-4 rounded p-3", styles.inputContainer)}
        >
          <Row className="align-items-center">
            <Col xs="auto">
              <Avatar width={48} src={user?.avatarUrl} />
            </Col>
            <Col xs="auto">
              <p className="m-0">
                <b>{user?.fullName}</b>
              </p>
              <Form.Group controlId="rating">
                <StarRating
                  active
                  numRating={+values.rating}
                  onChange={(val) => setFieldValue("rating", +val)}
                />
                {touched.rating || (touched.rating && errors.rating) ? (
                  <p className="text-left m-0 text-danger">
                    <small>{errors.rating}</small>
                  </p>
                ) : null}
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mt-3" controlId="content">
            <TextArea
              type="textarea"
              placeholder="Add a public comment"
              rows={2}
              value={values.content}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="off"
              touchedValue={touched.content}
              errorsValue={errors.content}
            />
          </Form.Group>

          <div className="ml-auto">
            {isEditMode && (
              <Button
                className="mr-2"
                variant="outline-primary"
                disabled={processingSubmit || processingUpdate}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={processingSubmit || processingUpdate || !submittable}
            >
              Review
            </Button>
          </div>
        </Form>
      ) : (
        <ReviewComment
          enrollment={{ user }}
          createdAt={userReview?.createdAt}
          rating={+values.rating}
          content={values.content}
          editable={editable}
          isLoading={!editable}
          className={clsx("mt-4 border", styles.myReview)}
          onEdit={handleClickEdit}
        />
      )}
    </AccessControl>
  );

  // variables

  const reviewsFiltered = reviews;

  // const reviewsFiltered = reviews
  //   ? isAuth
  //     ? reviews.filter((r) => r.id !== userReviewId)
  //     : reviews
  //   : [];

  return (
    <Container className="container-fluid p-0 py-5">
      <Row>
        <Col xs={12} lg={5} className="mb-5">
          <ReviewSummary
            title="Student Feedback"
            numRating={avgRating}
            ratingList={ratingList}
            ratingSelected={ratingSelected}
            onChange={(rating) => setRatingSelected(rating)}
            totalReviews={totalReviews}
          />
          {formReview}
        </Col>
        <Col xs={12} lg={7}>
          <div className="d-flex flex-wrap justify-content-between">
            <h5>Reviews</h5>
          </div>
          <div
            className={clsx("d-flex flex-column mt-4", styles.commentContainer)}
          >
            {processingFetch &&
              placeholder.map((val) => <ReviewCommentPlaceHolder key={val} />)}

            {reviewsFiltered &&
              reviewsFiltered?.length !== 0 &&
              reviewsFiltered.map((review, index) => {
                return <ReviewComment key={index} {...review} />;
              })}

            {!processingFetch &&
              (!reviewsFiltered || reviewsFiltered?.length === 0) && (
                <NoData title="No Review Found!" className="border-0" />
              )}

            {pagination.HasNext && reviewsFiltered?.length !== 0 && (
              <button
                className={styles.btnSeeMore}
                onClick={handleSeeMore}
                disabled={processingGetMore}
              >
                {processingGetMore ? <SpinnerCustom /> : "See More Reviews"}
              </button>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

/* required: display name */
ReviewsTab.displayName = "ReviewsTab";

/* required: props types */
ReviewsTab.propTypes = {
  courseId: PropTypes.string,
  loadReviews: PropTypes.bool,
};

/* required: default props */
ReviewsTab.defaultProps = { loadReviews: false };

export { ReviewsTab };
